// Timeline Sequencer Data Structures and Types

export interface SequenceEvent {
  id: string;
  timestamp: number; // in seconds
  shaderCode: string;
  title: string;
  description?: string;
  duration?: number; // optional duration for timed events
  parameters?: Record<string, number | number[]>;
  audioMappings?: any[]; // AudioMapping type from existing code
}

export interface TrackSequence {
  trackId: string;
  trackTitle: string;
  duration: number; // track duration in seconds
  events: SequenceEvent[];
}

export interface SequencerState {
  sequences: Record<string, TrackSequence>; // trackId -> TrackSequence
  currentTrackId: string;
  currentTime: number;
  isPlaying: boolean;
  selectedEventId?: string;
}

export class SequencerEngine {
  private sequences: Record<string, TrackSequence> = {};
  private currentTrackId: string = '';
  private currentTime: number = 0;
  private lastTriggeredEventId: string = '';
  private readonly STORAGE_KEY = 'timeline-sequences';
  
  constructor(
    private onShaderChange: (shaderCode: string, parameters?: Record<string, number | number[]>) => void
  ) {
    this.loadFromStorage();
  }

  // Load sequence data for a track
  setTrackSequence(trackId: string, sequence: TrackSequence) {
    this.sequences[trackId] = sequence;
  }

  // Update current playback state
  updatePlayback(trackId: string, currentTime: number, isPlaying: boolean) {
    this.currentTrackId = trackId;
    this.currentTime = currentTime;
    
    if (isPlaying) {
      this.checkForEvents();
    }
  }

  // Check if any events should be triggered at current time
  private checkForEvents() {
    const sequence = this.sequences[this.currentTrackId];
    if (!sequence) return;

    // Find the most recent event that should be active
    const activeEvent = sequence.events
      .filter(event => event.timestamp <= this.currentTime)
      .sort((a, b) => b.timestamp - a.timestamp)[0]; // Most recent first

    if (activeEvent && activeEvent.id !== this.lastTriggeredEventId) {
      this.triggerEvent(activeEvent);
      this.lastTriggeredEventId = activeEvent.id;
    }
  }

  // Trigger a specific event
  private triggerEvent(event: SequenceEvent) {
    console.log(`Triggering shader event: ${event.title} at ${event.timestamp}s`);
    this.onShaderChange(event.shaderCode, event.parameters);
  }

  // Add a new event to the timeline
  addEvent(trackId: string, event: SequenceEvent) {
    if (!this.sequences[trackId]) {
      this.sequences[trackId] = {
        trackId,
        trackTitle: trackId,
        duration: 300, // default 5 minutes
        events: []
      };
    }
    
    this.sequences[trackId].events.push(event);
    this.sequences[trackId].events.sort((a, b) => a.timestamp - b.timestamp);
    this.saveToStorage();
  }

  // Remove an event
  removeEvent(trackId: string, eventId: string) {
    const sequence = this.sequences[trackId];
    if (sequence) {
      sequence.events = sequence.events.filter(event => event.id !== eventId);
      this.saveToStorage();
    }
  }

  // Get events for a track
  getTrackEvents(trackId: string): SequenceEvent[] {
    return this.sequences[trackId]?.events || [];
  }

  // Get current sequence
  getCurrentSequence(): TrackSequence | null {
    return this.sequences[this.currentTrackId] || null;
  }

  // Seek to a specific time (for timeline scrubbing)
  seekTo(time: number) {
    this.currentTime = time;
    this.lastTriggeredEventId = ''; // Reset to allow re-triggering
    this.checkForEvents();
  }

  // Save sequences to localStorage
  private saveToStorage() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.sequences));
    } catch (error) {
      console.warn('Failed to save timeline sequences to localStorage:', error);
    }
  }

  // Load sequences from localStorage
  private loadFromStorage() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        this.sequences = JSON.parse(saved);
        console.log('Loaded timeline sequences from localStorage');
      }
    } catch (error) {
      console.warn('Failed to load timeline sequences from localStorage:', error);
    }
  }

  // Export timeline data as JSON
  exportTimeline(): string {
    return JSON.stringify({
      version: '1.0',
      exportDate: new Date().toISOString(),
      sequences: this.sequences
    }, null, 2);
  }

  // Import timeline data from JSON
  importTimeline(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      return this.importTimelineFromData(data);
    } catch (error) {
      console.error('Failed to parse timeline JSON:', error);
      return false;
    }
  }

  // Import timeline data from parsed JSON object
  importTimelineFromData(data: any): boolean {
    try {
      // Validate basic structure
      if (!data.sequences || typeof data.sequences !== 'object') {
        throw new Error('Invalid timeline data structure');
      }

      // Validate each sequence
      for (const [trackId, sequence] of Object.entries(data.sequences)) {
        const seq = sequence as any;
        if (!seq.trackId || !Array.isArray(seq.events)) {
          throw new Error(`Invalid sequence data for track ${trackId}`);
        }
      }

      this.sequences = data.sequences;
      this.saveToStorage();
      console.log('Timeline data imported successfully');
      return true;
    } catch (error) {
      console.error('Failed to import timeline data:', error);
      return false;
    }
  }

  // Download timeline as file
  downloadTimeline(filename: string = 'timeline-sequences.json') {
    const data = this.exportTimeline();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}